import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { ColorPickerModule } from 'ngx-color-picker';
import { MomentModule } from 'ngx-moment';

import { AuthGuard } from 'app/auth/auth-guard.service';
import { FormModule } from 'app/form/form.module';
import { FormFieldsModule } from 'app/form-fields/form-fields.module';
import { SegmentModule } from 'app/segment/segment.module';
import { ReleaseUpdateGuard } from 'app/shared/service/release-update-guard.service';
import { SharedModule } from 'app/shared/shared.module';
import { UIComponentsModule } from 'app/ui-components/ui-components.module';

import { ActivityListComponent } from './activity/activity-list.component';
import { AddTagGroupComponent } from './add-tag-group.component';
import { AddTemplateTagComponent } from './add-template-tag.component';
import { AddUserComponent } from './add-user.component';
import { AlignmentComponent } from './alignment.component';
import { CompanyHomeActivityComponent } from './company-home-activity.component';
import { CompanyHomePageComponent } from './company-home-page.component';
import { CompanyBillingLicensesTableComponent } from './company-settings/company-billing-settings/company-billing-licenses/company-billing-licenses.component';
import { CompanyBillingSettingsComponent } from './company-settings/company-billing-settings/company-billing-settings.component';
import { CompanyBrandSettingsComponent } from './company-settings/company-brand-settings/company-brand-settings.component';
import { CompanyDomainSettingsComponent } from './company-settings/company-domain-settings/company-domain-settings.component';
import { CompanyGeneralSettingsComponent } from './company-settings/company-general-settings/company-general-settings.component';
import { CompanySettingsComponent } from './company-settings/company-settings.component';
import { CompanySettingsBusinessAttributesSelectorComponent } from './company-settings-business-attributes-selector/company-settings-business-attributes-selector.component';
import { EngagementListComponent } from './engagement-list.component';
import { EngagementComponent } from './engagement.component';
import { ProjectHomeActivityComponent } from './project-home-activity.component';
import { ProjectHomePageComponent } from './project-home-page.component';
import { ProjectHomeSettingsComponent } from './project-home-settings.component';
import { ProjectTagGroupsComponent } from './project-tag-groups.component';
import { StatsService } from './stats.service';
import { TagGroupComponent } from './tag-group.component';
import { TeamHomeActivityComponent } from './team-home-activity.component';
import { TeamHomePageComponent } from './team-home-page.component';
import { TeamHomeSettingsComponent } from './team-home-settings.component';
import { TeamTagGroupsComponent } from './team-tag-groups.component';
import { TrialStartGuideAccordionComponent } from './trial-start-guide/trial-start-guide-accordion/trial-start-guide-accordion.component';
import { TrialStartGuideComponent } from './trial-start-guide/trial-start-guide.component';
import { MetaDataService } from '../shared/service/meta-data.service';

const homePageRouting: ModuleWithProviders<RouterModule> = RouterModule.forChild([
  {
    path: 'companies/:companyId',
    component: CompanyHomePageComponent,
    canActivate: [AuthGuard, ReleaseUpdateGuard],
    children: [
      { path: '', redirectTo: 'activity', pathMatch: 'full' },
      { path: 'activity', component: CompanyHomeActivityComponent },
      { path: 'engagement', redirectTo: 'activity' },
      {
        path: 'settings',
        component: CompanySettingsComponent,
        children: [
          { path: '', redirectTo: 'general', pathMatch: 'full' },
          { path: 'general', component: CompanyGeneralSettingsComponent },
          { path: 'branding', component: CompanyBrandSettingsComponent },
          { path: 'billing', component: CompanyBillingSettingsComponent },
        ],
      },
    ],
  },
  {
    path: 'projects/:projectId',
    component: ProjectHomePageComponent,
    canActivate: [AuthGuard, ReleaseUpdateGuard],
    children: [
      { path: '', redirectTo: 'activity', pathMatch: 'full' },
      { path: 'activity', component: ProjectHomeActivityComponent },
      { path: 'engagement', redirectTo: 'activity' },
      {
        path: 'settings',
        component: ProjectHomeSettingsComponent,
        children: [
          { path: '', redirectTo: 'tag-groups', pathMatch: 'full' },
          { path: 'alignment', component: AlignmentComponent },
          { path: 'tag-groups', component: ProjectTagGroupsComponent },
        ],
      },
    ],
  },
  {
    path: 'teams/:teamId',
    component: TeamHomePageComponent,
    canActivate: [AuthGuard, ReleaseUpdateGuard],
    children: [
      { path: '', redirectTo: 'activity', pathMatch: 'full' },
      { path: 'activity', component: TeamHomeActivityComponent },
      { path: 'engagement', redirectTo: 'activity' },
      {
        path: 'settings',
        component: TeamHomeSettingsComponent,
        children: [
          { path: '', redirectTo: 'tag-groups', pathMatch: 'full' },
          { path: 'tag-groups', component: TeamTagGroupsComponent },
        ],
      },
    ],
  },
  {
    path: 'teams/:teamId/editUserProfile',
    component: TeamHomePageComponent,
    canActivate: [AuthGuard, ReleaseUpdateGuard],
  },
]);

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    MomentModule,
    NgbModule,
    SharedModule,
    homePageRouting,
    FormModule,
    SegmentModule,
    FormFieldsModule,
    ColorPickerModule,
    NgSelectModule,
    UIComponentsModule,
    GoogleTagManagerModule,
    ReactiveFormsModule,
  ],
  declarations: [
    TeamHomePageComponent,
    CompanyHomePageComponent,
    ProjectHomePageComponent,
    ActivityListComponent,
    EngagementListComponent,
    EngagementComponent,
    AddUserComponent,
    TeamHomeActivityComponent,
    ProjectHomeActivityComponent,
    CompanyHomeActivityComponent,
    TeamHomeSettingsComponent,
    TeamTagGroupsComponent,
    TagGroupComponent,
    AddTagGroupComponent,
    ProjectHomeSettingsComponent,
    ProjectTagGroupsComponent,
    AlignmentComponent,
    CompanyGeneralSettingsComponent,
    CompanyBrandSettingsComponent,
    AddTemplateTagComponent,
    TrialStartGuideComponent,
    TrialStartGuideAccordionComponent,
    CompanySettingsBusinessAttributesSelectorComponent,
    CompanyDomainSettingsComponent,
    CompanySettingsComponent,
    CompanyBillingLicensesTableComponent,
    CompanyBillingSettingsComponent,
  ],
  providers: [StatsService, MetaDataService],
})
export class HomePageModule {}
